import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Login() {
  const { login, appearance } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await login(email, password); nav("/"); } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:flex flex-col justify-between bg-[#0B132B] text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?crop=entropy&cs=srgb&fm=jpg&q=60&w=1400)" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B] via-[#0B132B]/70 to-transparent" />
        <div className="relative flex items-center gap-3">
          <div className="h-9 w-9 rounded-sm bg-[#C5A059] text-[#0B132B] grid place-items-center"><Building2 size={20} /></div>
          <span className="font-semibold" style={{ fontFamily: "Manrope" }}>{appearance.app_name}</span>
        </div>
        <div className="relative max-w-md">
          <p className="eyebrow text-[#C5A059] mb-3">Public-signal prospect intelligence</p>
          <h1 className="text-3xl font-bold leading-tight">Find property buyers and investors from public web signals — transparently.</h1>
          <p className="text-slate-300 mt-4 text-sm leading-relaxed">Searches only publicly indexed pages and permitted sources. Every prospect carries its evidence and source URL. Financial signals are estimates from public professional indicators, never verified wealth.</p>
          <div className="mt-6 flex items-center gap-2 text-xs text-slate-400"><ShieldCheck size={14} className="text-emerald-400" /> No private scraping · No fabricated data · Admin-only access</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div>
            <p className="eyebrow mb-1">Admin sign in</p>
            <h2 className="text-2xl font-bold">Welcome back</h2>
          </div>
          <div>
            <label className="field-label">Email</label>
            <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          </div>
          <div>
            <label className="field-label">Password</label>
            <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {err && <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-sm px-3 py-2">{err}</div>}
          <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full bg-[#0B132B] hover:bg-[#15213D] text-white dark:bg-[#C5A059] dark:text-[#0B132B] dark:hover:bg-[#d4b06a]">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
